import * as turf from '@turf/turf';
import { gradientScaleLinear } from 'utils/colors/colorScale.utils';
import { toDegrees, toRadians } from 'utils/math/math.utils';
import { getValidMapPosition } from 'utils/maps/coordinate.utils';
import { isForecastStormRecord } from './hurricanes.utils';

// Geometry math for storm tracks, forecast cones, and historical outlines.
// All functions are pure: they take chronological storm records (or lat/lng
// points) and return Leaflet-ready [lat, lng] position arrays.

const EARTH_RADIUS_METERS = 6378137;
// Legacy tuning factor that converts the API radius into map-projected width.
const RADIUS_PROJECTION_FACTOR = 0.0176229;
const MILES_TO_METERS = 1609.34;
// Meters added to every forecast cone radius so short forecasts stay visible.
const CONE_BASE_PADDING_METERS = 50000;
// Fixed half-width of the historical storm outline.
const HISTORICAL_OUTLINE_RADIUS_METERS = 400000;
// Radius used for the rounded end caps of the historical outline.
const OUTLINE_END_CAP_RADIUS_METERS = 10000 / RADIUS_PROJECTION_FACTOR;

// Forecast-hour offsets mapped to expected track-error miles (NHC-style cone).
const CONE_FORECAST_HOURS = [0, 12, 24, 36, 48, 60, 72, 96, 120];
const CONE_ERROR_MILES = [0, 30, 45, 61, 77, 93, 114, 167, 236];

export const toLatLng = (latitude, longitude) => getValidMapPosition(latitude, longitude);

const toRadianPair = (point) => [toRadians(point[0]), toRadians(point[1])];

const getBearingRadians = ([lat1, lon1], [lat2, lon2]) => {
  const deltaLon = lon2 - lon1;
  const y = Math.sin(deltaLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLon);
  return Math.atan2(y, x);
};

const arePointsFinite = (points) => points.every((value) => Number.isFinite(value));

// Two points perpendicular to the track direction, radius meters either side.
export const getPerpendicularEdgePoints = (currentPoint, previousPoint, radiusMeters) => {
  const projectedRadius = radiusMeters * RADIUS_PROJECTION_FACTOR;
  const current = toRadianPair(currentPoint);
  const previous = toRadianPair(previousPoint);
  if (!arePointsFinite([...current, ...previous])) return [];

  const perpendicularBearing = getBearingRadians(current, previous) + Math.PI / 2;
  const lineLength = (projectedRadius / EARTH_RADIUS_METERS) * (180 / Math.PI);
  const [lat, lon] = current;

  return [
    [
      toDegrees(lat + lineLength * Math.cos(perpendicularBearing)),
      toDegrees(lon + lineLength * Math.sin(perpendicularBearing)),
    ],
    [
      toDegrees(lat - lineLength * Math.cos(perpendicularBearing)),
      toDegrees(lon - lineLength * Math.sin(perpendicularBearing)),
    ],
  ];
};

// Three points fanned around the track direction to cap a shape's end.
// scale < 0 flips the cap toward the start of the track.
const getEndCapPoints = (radiusMeters, currentPoint, previousPoint, capScale) => {
  const projectedRadius = radiusMeters * RADIUS_PROJECTION_FACTOR * capScale;
  const current = toRadianPair(currentPoint);
  const previous = toRadianPair(previousPoint);
  if (!arePointsFinite([...current, ...previous])) return [];

  const bearing = getBearingRadians(current, previous);
  const leftBearing = bearing + Math.PI / 4;
  const rightBearing = bearing + (7 * Math.PI) / 4;
  const lineLength = (projectedRadius / EARTH_RADIUS_METERS) * (180 / Math.PI);
  const [lat, lon] = current;

  const pointAt = (capBearing) => [
    toDegrees(lat - lineLength * Math.cos(capBearing)),
    toDegrees(lon - lineLength * Math.sin(capBearing)),
  ];

  return [pointAt(rightBearing), pointAt(bearing), pointAt(leftBearing)];
};

export const getLeadingEdgePoints = (radiusMeters, currentPoint, previousPoint) =>
  getEndCapPoints(radiusMeters, currentPoint, previousPoint, 1);

export const getTrailingEdgePoints = (radiusMeters, currentPoint, previousPoint) =>
  getEndCapPoints(radiusMeters, currentPoint, previousPoint, 0.5);

// Cone radius grows with the forecast-hour offset from the first forecast point.
export const getForecastConeRadiusMeters = (pointTime, baseTime) => {
  const hoursFromBase = (new Date(pointTime) - new Date(baseTime)) / 1000 / 60 / 60;
  const errorMiles = gradientScaleLinear().domain(CONE_FORECAST_HOURS).range(CONE_ERROR_MILES)(
    hoursFromBase,
  );

  return MILES_TO_METERS * errorMiles + CONE_BASE_PADDING_METERS;
};

export const buildStormTrackPath = (records = []) => {
  const pathCoordinates = records
    .map((record) => toLatLng(record.latitude, record.longitude))
    .filter(Boolean);

  return pathCoordinates.length < 2 ? [] : pathCoordinates;
};

// Produces visually distinct measured and forecast track segments. The
// forecast segment starts at the final observed point so the two paths remain
// connected without making the forecast portion look measured.
export const buildStormTrackSegments = (records = []) => {
  const observedRecords = records.filter((record) => !isForecastStormRecord(record));
  const forecastRecords = records.filter(isForecastStormRecord);
  const latestObservedRecord = observedRecords[observedRecords.length - 1] ?? null;
  const connectedForecastRecords = latestObservedRecord
    ? [latestObservedRecord, ...forecastRecords]
    : forecastRecords;

  return {
    observedTrackPositions: buildStormTrackPath(observedRecords),
    forecastTrackPositions: buildStormTrackPath(connectedForecastRecords),
  };
};

// Walks consecutive record pairs and collects the two ribbon edges plus the
// coordinates needed to cap the shape at both ends.
const buildRibbonEdges = (records, getRadiusMeters) => {
  const edgeLines = [];

  for (let index = 1; index < records.length; index += 1) {
    const current = toLatLng(records[index].latitude, records[index].longitude);
    const previous = toLatLng(records[index - 1].latitude, records[index - 1].longitude);
    if (!current || !previous) continue;

    const edge = getPerpendicularEdgePoints(current, previous, getRadiusMeters(records[index]));
    if (edge.length === 2) edgeLines.push(edge);
  }

  if (!edgeLines.length) return null;

  return {
    leftEdge: edgeLines.map((edge) => edge[0]),
    rightEdge: edgeLines.map((edge) => edge[1]),
    firstCoords: toLatLng(records[0].latitude, records[0].longitude),
    secondCoords: toLatLng(records[1]?.latitude, records[1]?.longitude),
    lastCoords: toLatLng(
      records[records.length - 1].latitude,
      records[records.length - 1].longitude,
    ),
    previousToLastCoords: toLatLng(
      records[records.length - 2]?.latitude,
      records[records.length - 2]?.longitude,
    ),
  };
};

const buildRawHistoricalOutline = (records) => {
  const ribbon = buildRibbonEdges(records, () => HISTORICAL_OUTLINE_RADIUS_METERS);
  if (!ribbon) return [];

  const { leftEdge, rightEdge, firstCoords, secondCoords, lastCoords, previousToLastCoords } =
    ribbon;
  if (!firstCoords || !secondCoords || !lastCoords || !previousToLastCoords) return [];

  const farCap = getLeadingEdgePoints(
    OUTLINE_END_CAP_RADIUS_METERS,
    lastCoords,
    previousToLastCoords,
  );
  const nearCap = getTrailingEdgePoints(OUTLINE_END_CAP_RADIUS_METERS, firstCoords, secondCoords);
  if (farCap.length < 3 || nearCap.length < 3) return [];

  return [
    [nearCap[0][0], nearCap[0][1]],
    ...nearCap,
    ...leftEdge,
    ...farCap,
    ...[...rightEdge].reverse(),
    [nearCap[0][0], nearCap[0][1]],
  ];
};

const buildRawForecastCone = (records) => {
  const forecastRecords = records.filter((record) => record.type === 'forecast');
  if (forecastRecords.length < 2) return [];

  const baseTime = forecastRecords[0].utc_time;
  const ribbon = buildRibbonEdges(forecastRecords, (record) =>
    getForecastConeRadiusMeters(record.utc_time, baseTime),
  );
  if (!ribbon) return [];

  const { leftEdge, rightEdge, firstCoords, lastCoords, previousToLastCoords } = ribbon;
  if (!firstCoords || !lastCoords || !previousToLastCoords) return [];

  const lastRecord = forecastRecords[forecastRecords.length - 1];
  const farCap = getLeadingEdgePoints(
    getForecastConeRadiusMeters(lastRecord.utc_time, baseTime),
    lastCoords,
    previousToLastCoords,
  );
  if (farCap.length < 3) return [];

  return [firstCoords, ...leftEdge, ...farCap, ...[...rightEdge].reverse()];
};

// Drops invalid points and closes the ring so turf accepts the polygon.
export const closeShapePoints = (shape = []) => {
  const validPoints = shape.filter(
    (point) => Array.isArray(point) && Number.isFinite(point[0]) && Number.isFinite(point[1]),
  );

  if (validPoints.length < 2) return validPoints;

  const [firstLat, firstLng] = validPoints[0];
  const [lastLat, lastLng] = validPoints[validPoints.length - 1];

  if (firstLat !== lastLat || firstLng !== lastLng) {
    return [...validPoints, validPoints[0]];
  }

  return validPoints;
};

// Heavy smoothing used for the historical outline: smooth, simplify, and
// union away self-intersections created by sharp track turns.
export const smoothClosedShape = (shape) => {
  const closedShape = closeShapePoints(shape);
  if (closedShape.length < 4) return closedShape;

  const smoothed = turf.polygonSmooth(turf.polygon([closedShape]), { iterations: 3 });
  const simplified = turf.simplify(smoothed, { tolerance: 0.5, highQuality: true });
  const resmoothed = turf.polygonSmooth(simplified, { iterations: 3 });

  const dedupedShape = turf.union(
    turf.featureCollection([
      turf.polygon(resmoothed.features[0].geometry.coordinates),
      turf.polygon(resmoothed.features[0].geometry.coordinates),
    ]),
  );

  const finalSimplified = turf.simplify(dedupedShape, { tolerance: 0.8, highQuality: true });
  const finalSmoothed = turf.polygonSmooth(finalSimplified, { iterations: 3 });
  return finalSmoothed.features[0].geometry.coordinates[0];
};

// Light smoothing for forecast cones; keeps the cone anchored on its origin.
export const smoothShapeKeepingOrigin = (shape) => {
  const closedShape = closeShapePoints(shape);
  if (closedShape.length < 4) return closedShape;

  const smoothed = turf.polygonSmooth(turf.polygon([closedShape]), { iterations: 3 });
  return smoothed.features[0].geometry.coordinates[0];
};

// Final closed polygon for a live storm's forecast cone, or null.
export const buildForecastCone = (records = []) => {
  const cone = smoothShapeKeepingOrigin(buildRawForecastCone(records));
  if (cone.length < 3) return null;
  return [...cone, cone[0]];
};

// Final closed polygon around a historical storm's full track, or null.
export const buildHistoricalStormOutline = (records = []) => {
  const outline = smoothClosedShape(buildRawHistoricalOutline(records));
  if (outline.length < 3) return null;
  return [...outline, outline[0]];
};
